import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Sparkles, Utensils, Tag, Calendar, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [angebotOpen, setAngebotOpen] = useState(false);
  const [mobileAngebotOpen, setMobileAngebotOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAngebotOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToSection = (sectionId) => {
    setIsOpen(false);
    setAngebotOpen(false);
    setMobileAngebotOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${sectionId}`);
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const navLinks = [
    { label: "Start", section: "hero" },
    { label: "Über mich", section: "about" },
    { label: "TM7", section: "tm7" },
  ];

  const angebotLinks = [
    { label: "Leistungen", section: "erlebniskochen", desc: "Vorführung & Erlebniskochen", icon: Utensils },
    { label: "Preise", section: "preise", desc: "Modelle & Finanzierung", icon: Tag },
    { label: "Aktuelle Angebote", section: "angebote", desc: "Befristete TM7-Sonderpreise", icon: Sparkles, badge: "HOT" },
    { label: "Kochkurs-Termine", section: "kochkurs-termine", desc: "12 kostenlose Kurse in Kempen", icon: Calendar, badge: "2026" },
    { label: "Einsteiger-Programm", section: "einsteiger", desc: "Dein Start – aktuelle Flyer", icon: Sparkles },
    { label: "Comeback-Aktion", section: "comeback", desc: "5 Termine bis 18.05.", icon: Users, badge: "VERLÄNGERT" },
  ];

  const afterNavLinks = [
    { label: "Standorte", section: "standorte" },
    { label: "FAQ", section: "faq" },
    { label: "Kontakt", section: "contact" },
  ];

  return (
    <header
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "glass-nav py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 text-slate-900 group">
            <img src="/logo.png" alt="Jennifer Laak – Thermomix Beratung Krefeld" className="w-12 h-12 rounded-full object-cover shadow-lg shadow-emerald-500/20 ring-2 ring-emerald-400/30 bg-white" />
            <div className="hidden sm:block leading-none whitespace-nowrap">
              <span className="font-bold text-lg font-[Outfit] block">JENNIFER LAAK</span>
              <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Thermomix® Repräsentantin</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8 xl:gap-10">
            {navLinks.map((link) => (
              <button
                key={link.section}
                onClick={() => scrollToSection(link.section)}
                data-testid={`nav-${link.section}`}
                className="text-slate-600 hover:text-emerald-600 transition-colors duration-200 font-medium text-sm relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-emerald-500 after:transition-all after:duration-300 hover:after:w-full"
              >
                {link.label}
              </button>
            ))}

            {/* Angebot Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setAngebotOpen((o) => !o)}
                onMouseEnter={() => setAngebotOpen(true)}
                data-testid="nav-angebot"
                className="flex items-center gap-1 text-slate-600 hover:text-emerald-600 transition-colors duration-200 font-medium text-sm"
                aria-expanded={angebotOpen}
              >
                Angebot
                <ChevronDown className={`w-4 h-4 transition-transform ${angebotOpen ? "rotate-180" : ""}`} />
              </button>

              {angebotOpen && (
                <div
                  onMouseLeave={() => setAngebotOpen(false)}
                  data-testid="nav-angebot-dropdown"
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-80 glass-strong rounded-2xl shadow-2xl shadow-slate-900/10 border border-white/40 p-2 z-50"
                >
                  {angebotLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <button
                        key={link.section}
                        onClick={() => scrollToSection(link.section)}
                        data-testid={`nav-angebot-${link.section}`}
                        className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-emerald-50/70 transition-colors text-left group"
                      >
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
                          <Icon className="w-4 h-4 text-emerald-600 group-hover:text-white transition-colors" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900 text-sm">{link.label}</span>
                            {link.badge && (
                              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500 text-white">
                                {link.badge}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">{link.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {afterNavLinks.map((link) => (
              <button
                key={link.section}
                onClick={() => scrollToSection(link.section)}
                data-testid={`nav-${link.section}`}
                className="text-slate-600 hover:text-emerald-600 transition-colors duration-200 font-medium text-sm relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-emerald-500 after:transition-all after:duration-300 hover:after:w-full"
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="hidden lg:block">
            <Button onClick={() => scrollToSection("contact")} data-testid="nav-cta-button" className="btn-primary px-7 py-2.5 text-sm">
              Jetzt Termin sichern
            </Button>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-slate-900 hover:bg-slate-100 rounded-xl transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isOpen && (
          <div data-testid="mobile-menu" className="lg:hidden absolute top-full left-0 right-0 glass-strong border-t border-white/20 max-h-[80vh] overflow-y-auto">
            <div className="container-custom py-6 space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.section}
                  onClick={() => scrollToSection(link.section)}
                  data-testid={`mobile-nav-${link.section}`}
                  className="block w-full text-left py-3 px-4 text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl font-medium transition-all"
                >
                  {link.label}
                </button>
              ))}

              {/* Mobile Angebot Accordion */}
              <button
                onClick={() => setMobileAngebotOpen((o) => !o)}
                data-testid="mobile-nav-angebot"
                className="w-full flex items-center justify-between py-3 px-4 text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl font-medium transition-all"
                aria-expanded={mobileAngebotOpen}
              >
                <span>Angebot</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${mobileAngebotOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileAngebotOpen && (
                <div className="pl-4 space-y-1 mb-2">
                  {angebotLinks.map((link) => (
                    <button
                      key={link.section}
                      onClick={() => scrollToSection(link.section)}
                      data-testid={`mobile-nav-angebot-${link.section}`}
                      className="w-full text-left py-2.5 px-4 text-sm text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all flex items-center gap-2"
                    >
                      <span>{link.label}</span>
                      {link.badge && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500 text-white">
                          {link.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {afterNavLinks.map((link) => (
                <button
                  key={link.section}
                  onClick={() => scrollToSection(link.section)}
                  data-testid={`mobile-nav-${link.section}`}
                  className="block w-full text-left py-3 px-4 text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl font-medium transition-all"
                >
                  {link.label}
                </button>
              ))}

              <div className="pt-4">
                <Button onClick={() => scrollToSection("contact")} data-testid="mobile-cta-button" className="w-full btn-primary py-4 text-base">
                  Jetzt Termin sichern
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navigation;
